import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AuthoritiesEnum, IntegrationTypeEnum } from "~/types/enums";
import { Alert, Badge, Box, Button, Card, Grid, Icon, InputCheckbox, InputText, InputToggle, Link, Picture, Spinner, Text } from "~/components-shared";
import integrationsActionCreators from "~/store/integrations/actions";
import integrationSelectors from "~/store/integrations/selectors";
import useAbacExt from "~/hooks/abac";
import api from "~/lib/api";
import asanaLogo from "~/assets/i/apps/asana_logo.svg";
import { handleError } from "~/lib/helpers";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import lists from "~/lib/lists";
const Actions = styled.div`
  position: relative;
  z-index: 2;
`;
const StyledGrid = styled(Grid)`
  position: relative;
  z-index: 2;
  max-height: 300px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`;
const authorities = [AuthoritiesEnum.INTEGRATIONS_WRITE, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE, AuthoritiesEnum.SIGN_UP_INTEGRATE];
const AsanaIntegrationCard = _ref => {
  let {
    ...rest
  } = _ref;
  const {
    t
  } = useTranslation();
  const [search, setSearch] = useState("");
  const {
    userHasOneOf
  } = useAbacExt();
  const dispatch = useDispatch();
  const integration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.ASANA));
  const [asanaWorkspaces, setAsanaWorkspaces] = useState([]);
  const [loadingAsanaWorkspaces, setLoadingAsanaWorkspaces] = useState(false);
  const [asanaWorkspacesError, setAsanaWorkspacesError] = useState();
  const [isExpanded, setIsExpanded] = useState(false);
  const handleLoadAsanaTeams = () => {
    setLoadingAsanaWorkspaces(true);
    setAsanaWorkspacesError(undefined);
    api.fetchAsanaWorkspaces().then(data => setAsanaWorkspaces(data)).catch(e => setAsanaWorkspacesError(handleError(e))).finally(() => setLoadingAsanaWorkspaces(false));
  };
  useEffect(() => {
    if (integration && userHasOneOf(authorities)) {
      handleLoadAsanaTeams();
    }
  }, [!!integration]);
  const handleToggle = value => {
    if (value) {
      api.asanaInstall().then(_ref2 => {
        let {
          path
        } = _ref2;
        const width = 560;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;
        window.open(path, "Install Asana", `width=${width},height=${height},left=${left},top=${top}`);
      });
      setIsExpanded(true);
    } else if (integration?.id) {
      dispatch(integrationsActionCreators.deleteStarted(integration.id));
    }
  };
  const handleExpand = () => {
    setIsExpanded(prev => !prev);
  };
  const handleUpdateWorkspace = workspaceId => {
    if (integration) {
      dispatch(integrationsActionCreators.updateStarted(integration.id, {
        type: integration.type,
        token: integration.token,
        settings: {
          ...integration.settings,
          workspace: workspaceId
        }
      }));
    }
  };
  const handleUpdate = function (projectIds) {
    let force = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    let deselect = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    if (integration) {
      let projects = integration.settings?.projects || [];
      projectIds.forEach(projectId => {
        if (projects.includes(projectId) && !force || deselect && force) {
          projects = projects.filter(id => id !== projectId);
        } else {
          projects.push(projectId);
        }
      });
      dispatch(integrationsActionCreators.updateStarted(integration.id, {
        type: integration.type,
        token: integration.token,
        settings: {
          ...integration.settings,
          projects
        }
      }));
    }
  };
  const filteredWorkspaces = asanaWorkspaces.map(space => ({
    ...space,
    projects: space.projects.filter(project => project.name.toLowerCase().includes(search.toLowerCase()) || project.teamName.toLowerCase().includes(search.toLowerCase()))
  }));
  const hasFiltered = filteredWorkspaces.length && search;
  return /*#__PURE__*/React.createElement(Card, _extends({
    onClick: userHasOneOf(authorities) && integration ? handleExpand : undefined,
    elemBefore: /*#__PURE__*/React.createElement(Picture, {
      src: asanaLogo,
      width: "30px",
      height: "30px",
      border: "squared",
      color: "transparent",
      s: {
        mr: 2
      },
      backgroundSize: "contain"
    }),
    title: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      element: "h3",
      display: "inline-block"
    }, "Asana"), integration && userHasOneOf(authorities) && /*#__PURE__*/React.createElement(Icon, {
      scale: 4,
      src: "chevron",
      rotate: isExpanded ? 0 : 180,
      s: {
        ml: 1,
        mb: 1
      }
    })),
    elemAfter: /*#__PURE__*/React.createElement(Actions, null, /*#__PURE__*/React.createElement(InputToggle, {
      value: !!integration,
      display: "inline-block",
      onChange: (value, event) => {
        event.stopPropagation();
        handleToggle(value);
      },
      disabled: !userHasOneOf(authorities)
    })),
    collapsed: !integration || !isExpanded,
    s: {
      mb: 3
    }
  }, rest), asanaWorkspacesError && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, asanaWorkspacesError), integration?.settings && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    display: "block",
    s: {
      mb: 3
    }
  }, "The following projects are selected:"), /*#__PURE__*/React.createElement(InputText, {
    onClick: e => e.stopPropagation(),
    s: {
      mb: 4
    },
    value: search,
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "search",
      s: {
        mr: 2
      }
    }),
    placeholder: t("common.actions.search"),
    onChange: val => setSearch(val),
    elemAfter: /*#__PURE__*/React.createElement(Box, {
      gap: 1,
      s: {
        ml: 1
      }
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: e => {
        e.stopPropagation();
        handleUpdate(filteredWorkspaces.flatMap(r => r.projects.map(p => p.id)), true);
      },
      color: "translucent",
      outline: false,
      fontSize: 3,
      s: {
        px: 3,
        py: 1
      },
      rounded: true
    }, hasFiltered ? "Select found" : "Select all"), /*#__PURE__*/React.createElement(Button, {
      onClick: e => {
        e.stopPropagation();
        handleUpdate(filteredWorkspaces.flatMap(r => r.projects.map(p => p.id)), true, true);
      },
      color: "translucent",
      fontSize: 3,
      outline: false,
      s: {
        px: 3,
        py: 1
      },
      rounded: true
    }, hasFiltered ? "Deselect found" : "Deselect all")),
    clearButton: true,
    fullWidth: true
  }), loadingAsanaWorkspaces ? /*#__PURE__*/React.createElement(Spinner, {
    center: true
  }) : /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 1,
    template: "1fr",
    s: {
      pl: 1
    },
    justify: "start",
    fullWidth: true
  }, filteredWorkspaces.length ? filteredWorkspaces.map(workspace => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(InputCheckbox, {
    key: workspace.id,
    value: integration?.settings?.workspace === workspace.id,
    onChange: () => handleUpdateWorkspace(workspace.id),
    label: `${workspace.name} (${workspace.isOrg ? "Organisation" : "Workspace"})`,
    s: {
      mb: 3
    }
  }), /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    template: "1fr minmax(auto, 150px) auto",
    gap: 3,
    fullWidth: true,
    s: {
      pr: 3
    }
  }, workspace.projects.map(project => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(InputCheckbox, {
    key: project.id,
    value: integration?.settings?.webhooks?.map(_ref3 => {
      let {
        resourceId
      } = _ref3;
      return resourceId;
    }).includes(project.id) && integration?.settings?.projects.includes(project.id),
    onChange: () => handleUpdate([project.id]),
    label: /*#__PURE__*/React.createElement(Link, {
      href: project.url,
      fontSize: 4,
      color: "text"
    }, project.name),
    s: {
      ml: 5
    }
  }), /*#__PURE__*/React.createElement(Grid.Cell, {
    justify: "end",
    align: "center"
  }, /*#__PURE__*/React.createElement(Text, {
    display: "block",
    color: "textSecondary"
  }, project.teamName)), /*#__PURE__*/React.createElement(Grid.Cell, {
    justify: "end",
    align: "center"
  }, project.completed ? /*#__PURE__*/React.createElement(Badge, {
    color: "translucent"
  }, "Completed \u2022 ", lists.asana.privacy.names[project.privacy]) : /*#__PURE__*/React.createElement(Badge, {
    color: "translucentSuccess"
  }, "Ongoing \u2022 ", lists.asana.privacy.names[project.privacy]))))))) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary"
  }, "No Workspaces"))));
};
export default AsanaIntegrationCard;